(() => {
  const selectionLabels = {
    tankless: {
      lighter: 'Lighter hot-water demand',
      everyday: 'Everyday hot-water demand',
      higher: 'Higher hot-water demand',
    },
    storage: {
      compact: 'Compact stored-water reserve',
      everyday: 'Everyday stored-water reserve',
      more: 'Larger stored-water reserve',
    },
  };

  const suggestedModels = {
    tankless: { lighter: '11 kW', everyday: '14 kW', higher: '18 kW' },
    storage: { compact: '2.5 gal', everyday: '4 gal', more: '8 gal' },
  };

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const initializeFitFinder = (root) => {
    if (root.dataset.elxFitReady === 'true') return;

    const mode = root.dataset.elxFitMode;
    const needStage = root.querySelector('[data-elx-fit-stage="0"]');
    const modelStage = root.querySelector('[data-elx-fit-stage="1"]');
    const resultStage = root.querySelector('[data-elx-fit-result-stage]');
    const results = [...root.querySelectorAll('[data-elx-fit-result]')];
    const progress = [...root.querySelectorAll('[data-elx-fit-progress]')];
    const decisionActions = root.querySelector('[data-elx-fit-decision-actions]');
    const resultActions = root.querySelector('[data-elx-fit-result-actions]');
    const continueButton = root.querySelector('[data-elx-fit-continue]');
    const resultBackButton = root.querySelector('[data-elx-fit-back-result]');
    const restartButton = root.querySelector('[data-elx-fit-restart]');
    const backButton = root.querySelector('[data-elx-fit-back]');
    const choice = {};
    let isShowingResult = false;

    const setHidden = (element, hidden) => {
      if (element) element.hidden = hidden;
    };

    const animateEntrance = (element) => {
      if (!element || reducedMotion()) return;
      element.classList.remove('is-entering');
      window.requestAnimationFrame(() => {
        element.classList.add('is-entering');
        window.setTimeout(() => element.classList.remove('is-entering'), 700);
      });
    };

    const updateProgress = () => {
      progress.forEach((item) => {
        const itemStep = Number(item.dataset.elxFitProgress);
        item.classList.toggle('is-active', itemStep === (isShowingResult ? 1 : 0));
        item.classList.toggle('is-complete', itemStep < (isShowingResult ? 1 : 0));
      });
      root.dataset.elxFitStep = isShowingResult ? '1' : '0';
    };

    const updateControls = () => {
      if (!continueButton) return;
      continueButton.disabled = !choice.need;
      continueButton.textContent = 'See my recommendation';
    };

    const selectCard = (card) => {
      if (!card) return;
      const options = card.closest('[data-elx-fit-options]');
      if (!options) return;
      options.querySelectorAll('[data-elx-fit-card]').forEach((item) => {
        const selected = item === card;
        item.classList.toggle('is-selected', selected);
        item.setAttribute('aria-pressed', String(selected));
      });
      choice.need = {
        value: card.dataset.elxFitValue,
        label: card.dataset.elxFitLabel,
      };
      root.dataset.elxFitSelected = 'need';
      if (!reducedMotion()) {
        card.animate(
          [{ transform: 'scale(.985)' }, { transform: 'scale(1.018)' }, { transform: 'scale(1)' }],
          { duration: 280, easing: 'cubic-bezier(.2,.8,.2,1)' },
        );
      }
      updateControls();
    };

    const renderNeed = (focus) => {
      isShowingResult = false;
      setHidden(needStage, false);
      setHidden(modelStage, true);
      setHidden(resultStage, true);
      setHidden(decisionActions, false);
      setHidden(resultActions, true);
      setHidden(backButton, true);
      results.forEach((result) => setHidden(result, true));
      updateProgress();
      updateControls();
      animateEntrance(needStage);
      if (focus) needStage?.querySelector('h3, [tabindex="-1"]')?.focus({ preventScroll: true });
    };

    const renderResult = () => {
      if (!choice.need) return;
      const model = suggestedModels[mode]?.[choice.need.value];
      const selectedResult = results.find((item) => String(item.dataset.elxFitResult || '').trim() === model);
      if (!model || !selectedResult) return;

      isShowingResult = true;
      setHidden(needStage, true);
      setHidden(modelStage, true);
      setHidden(decisionActions, true);
      setHidden(resultActions, false);
      setHidden(resultStage, false);
      results.forEach((result) => setHidden(result, result !== selectedResult));
      selectedResult.querySelector('[data-elx-fit-choice="need"]').textContent = selectionLabels[mode][choice.need.value];
      selectedResult.querySelector('[data-elx-fit-choice="model"]').textContent = model;
      selectedResult.querySelector('[data-elx-fit-reason]').textContent = mode === 'tankless'
        ? `Based on your ${selectionLabels[mode][choice.need.value].toLowerCase()} selection, ${model} is a model to explore first. Review the product specifications and confirm electrical requirements before installation.`
        : `Based on your ${selectionLabels[mode][choice.need.value].toLowerCase()} selection, ${model} is a model to explore first. Review the product specifications and confirm the point-of-use installation requirements before installation.`;
      updateProgress();
      animateEntrance(resultStage);
      resultStage?.querySelector('h3')?.focus({ preventScroll: true });
    };

    const reset = () => {
      delete choice.need;
      delete root.dataset.elxFitSelected;
      root.querySelectorAll('[data-elx-fit-stage="0"] [data-elx-fit-card]').forEach((item) => {
        item.classList.remove('is-selected');
        item.setAttribute('aria-pressed', 'false');
      });
      if (mode === 'tankless') {
        selectCard(root.querySelector('[data-elx-fit-stage="0"] [data-elx-fit-card][data-elx-fit-value="everyday"]'));
      }
      renderNeed(true);
      root.scrollIntoView({ block: 'start', behavior: reducedMotion() ? 'auto' : 'smooth' });
    };

    const modelProgress = progress.find((item) => item.dataset.elxFitProgress === '1');
    const resultProgress = progress.find((item) => item.dataset.elxFitProgress === '2');
    if (modelProgress) modelProgress.hidden = true;
    if (resultProgress) {
      resultProgress.dataset.elxFitProgress = '1';
      resultProgress.textContent = 'Recommendation';
    }

    root.querySelectorAll('[data-elx-fit-stage="0"] [data-elx-fit-card]').forEach((card) => {
      card.addEventListener('click', () => selectCard(card));
      card.addEventListener('keydown', (event) => {
        if (!['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(event.key)) return;
        const cards = [...card.closest('[data-elx-fit-options]').querySelectorAll('[data-elx-fit-card]')];
        const direction = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
        const next = cards[(cards.indexOf(card) + direction + cards.length) % cards.length];
        event.preventDefault();
        next.focus();
        selectCard(next);
      });
    });

    continueButton?.addEventListener('click', renderResult);
    resultBackButton?.addEventListener('click', () => renderNeed(true));
    restartButton?.addEventListener('click', reset);

    root.dataset.elxFitReady = 'true';
    if (mode === 'tankless') {
      selectCard(root.querySelector('[data-elx-fit-stage="0"] [data-elx-fit-card][data-elx-fit-value="everyday"]'));
    }
    renderNeed(false);
  };

  const initialize = () => document.querySelectorAll('[data-elx-heater-fit]').forEach(initializeFitFinder);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
